import { asyncTimeout } from '@caresend/utils';
import { defineComponent, ref, watch, watchEffect, computed } from 'vue';
import __vue_component__ from '../../alerts/Alert.vue.js';
import __vue_component__$1 from '../../alerts/Pane/Pane.vue.js';
import __vue_component__$2 from '../../transitions/TransitionTranslate/TransitionTranslate.vue.js';
import __vue_component__$3 from './components/TransitionOnResize.vue.js';
import { useElementChildren, useMediaQuery } from '../../../composables/dom.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../router/helpers/initRouter.js';

/** @deprecated Use SuperPopover with `useInteractionPosition` instead */
var script = defineComponent({
    name: 'InteractivePopup',
    components: {
        Alert: __vue_component__,
        Pane: __vue_component__$1,
        TransitionTranslate: __vue_component__$2,
        TransitionOnResize: __vue_component__$3,
    },
    model: {
        prop: 'isOpened',
        event: 'update',
    },
    props: {
        containerComponent: {
            type: [Object, String],
            default: 'span',
        },
        isOpened: {
            type: Boolean,
            default: false,
        },
        disabled: Boolean,
        justify: {
            type: String,
            default: 'start',
        },
        isBottomBoundMobile: Boolean,
        /** Used for mobile bottom bound */
        title: {
            type: String,
            default: '',
        },
        offset: {
            type: Object,
            default: () => ({ x: 0, y: 0 }),
        },
    },
    emits: ['disabledAction', 'close', 'update', 'cancel'],
    setup(props, { emit }) {
        const isOpenedState = ref(props.isOpened);
        const isContentVisible = ref(props.isOpened);
        const triggerSlot = ref(null);
        const contentDimensions = ref({ height: 0, width: 0 });
        const containerRef = ref(null);
        const { children } = useElementChildren(containerRef, ['.animation-content']);
        const popoverPosition = ref({ x: 0, y: 0 });
        const syncPopupWithSlot = () => {
            var _a, _b;
            if (!triggerSlot.value || !containerRef.value)
                return;
            const modal = children.value['.animation-content'];
            const leftOffset = (_a = modal === null || modal === void 0 ? void 0 : modal.getBoundingClientRect().left) !== null && _a !== void 0 ? _a : 0;
            const topOffset = (_b = modal === null || modal === void 0 ? void 0 : modal.getBoundingClientRect().top) !== null && _b !== void 0 ? _b : 0;
            const triggerRect = triggerSlot.value.getBoundingClientRect();
            popoverPosition.value = {
                x: triggerRect.left - leftOffset + props.offset.x,
                y: triggerRect.bottom - topOffset + props.offset.y + 8,
            };
        };
        /** "Clamps" a value between a minimum and maximum value */
        const clamp = (value, { min, max, }) => Math.min(Math.max(value, min), max);
        const adjustForOutOfBounds = () => {
            var _a, _b;
            if (!contentDimensions.value || !triggerSlot.value)
                return;
            const modal = children.value['.animation-content'];
            const contentRect = contentDimensions.value;
            const modalRect = modal === null || modal === void 0 ? void 0 : modal.getBoundingClientRect();
            const currentLeft = popoverPosition.value.x;
            const currentTop = popoverPosition.value.y;
            const adjustedLeft = clamp(currentLeft, {
                min: 0,
                max: ((_a = modalRect === null || modalRect === void 0 ? void 0 : modalRect.width) !== null && _a !== void 0 ? _a : window.innerWidth) - contentRect.width,
            });
            const adjustedTop = clamp(currentTop, {
                min: 0,
                max: ((_b = modalRect === null || modalRect === void 0 ? void 0 : modalRect.height) !== null && _b !== void 0 ? _b : window.innerHeight) - contentRect.height - 50,
            });
            const { width: triggerWidth } = triggerSlot.value.getBoundingClientRect();
            let alignmentAdjustment = 0;
            if (adjustedTop !== currentTop) {
                if (props.justify !== 'end')
                    alignmentAdjustment = 0;
                else
                    alignmentAdjustment = -triggerWidth;
            }
            popoverPosition.value = {
                x: adjustedLeft + alignmentAdjustment + props.offset.x,
                y: adjustedTop + props.offset.y,
            };
        };
        const accountForAlignment = () => {
            if (props.justify === 'end' && contentDimensions.value && triggerSlot.value) {
                const { x, y } = popoverPosition.value;
                const { width } = contentDimensions.value;
                const { width: triggerWidth } = triggerSlot.value.getBoundingClientRect();
                popoverPosition.value = {
                    x: x - width + triggerWidth,
                    y,
                };
            }
        };
        watch(() => props.isOpened, async () => {
            if (props.isOpened === isOpenedState.value)
                return;
            if (props.isOpened) {
                isOpenedState.value = true;
                await asyncTimeout(0);
                isContentVisible.value = true;
                syncPopupWithSlot();
                accountForAlignment();
                adjustForOutOfBounds();
            }
            else {
                isContentVisible.value = false;
                await asyncTimeout(30);
                isOpenedState.value = false;
            }
        }, { immediate: true });
        watchEffect(() => {
            emit('update', isOpenedState.value);
            if (!isOpenedState.value)
                emit('close');
        });
        const emitCancel = () => {
            emit('cancel');
        };
        const closePopup = async (withTimeout = true) => {
            isContentVisible.value = false;
            if (withTimeout)
                await asyncTimeout(200);
            isOpenedState.value = false;
            emitCancel();
        };
        const openPopup = async () => {
            if (props.disabled) {
                emit('disabledAction');
                return;
            }
            isOpenedState.value = true;
            await asyncTimeout(0);
            syncPopupWithSlot();
            accountForAlignment();
            adjustForOutOfBounds();
            await asyncTimeout(0);
            isContentVisible.value = true;
        };
        const { matches: isMobile } = useMediaQuery('mobile');
        const contentClasses = computed(() => {
            const classes = [];
            if (isContentVisible.value)
                classes.push('InteractivePopup--visible');
            return classes;
        });
        const transitionTranslateClasses = computed(() => {
            const classes = [];
            classes.push('InteractivePopup__transition-translate--hidden');
            return classes;
        });
        return {
            contentDimensions,
            containerRef,
            contentClasses,
            closePopup,
            emitCancel,
            isContentVisible,
            isOpenedState,
            openPopup,
            popoverPosition,
            triggerSlot,
            isMobile,
            adjustForOutOfBounds,
            transitionTranslateClasses,
        };
    },
});

export { script as default };
